import { Alert, AlertAction, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Stepper } from 'components/ui/stepper';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import { BellRingIcon } from 'lucide-react';
import posthog from 'posthog-js';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import Platform from 'utils/Platform';

/**
 * Where the patient's first exchange stands, from her first message until the
 * caregiver answers it, with the offer to be notified of the answer (Figma
 * 2d-Messaging-FirstConversation, "Et maintenant").
 */
export default function ConversationProgress({ caregiverFirstName }: { caregiverFirstName: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { doesUserHaveNotifications } = useCheckNotificationsPermissions();
  const { enableNotifications } = usePushNotificationOptIn();

  const values = { firstName: caregiverFirstName };

  return (
    <div className="flex flex-col gap-4 px-4" data-testid="conversation-progress">
      <Card variant="outline" className="gap-4 pt-4 pb-6">
        <CardContent className="flex flex-col gap-4 px-6">
          <p className="text-base text-muted-foreground">{t('messagingV3.conversation.progressDelay', values)}</p>
          <Stepper
            steps={[
              { label: t('messagingV3.conversation.progressWritten'), state: 'done' },
              { label: t('messagingV3.conversation.progressReading', values), state: 'current' },
              { label: t('messagingV3.conversation.progressOrient'), state: 'upcoming' },
            ]}
          />
          <p className="text-sm text-muted-foreground">{t('messagingV3.conversation.progressEmergency')}</p>
        </CardContent>
      </Card>
      {!doesUserHaveNotifications && (
        // A single line beside its button: everything centred on one row
        <Alert variant="elevated" className="items-center [&>svg]:translate-y-0" data-testid="notify-me-alert">
          <BellRingIcon aria-hidden />
          <AlertTitle className="line-clamp-none font-normal">
            {t('messagingV3.conversation.notify', values)}
          </AlertTitle>
          <AlertAction className="row-span-1">
            <Button
              variant="inverse"
              size="sm"
              onClick={() => {
                posthog.capture('notification_suggester_clicked', { source: 'conversation_progress' });
                if (Platform.isNative()) {
                  enableNotifications();
                } else {
                  navigate(routes.settings.route);
                }
              }}
            >
              {t('messagingV3.conversation.notifyAction')}
            </Button>
          </AlertAction>
        </Alert>
      )}
    </div>
  );
}
